import React, { useMemo } from 'react';
import {
  buildCumulativeSubscriberTrend,
  chartXAtPeriodIndex,
  findCurrentPeriodIndex,
  formatTrendPeriodTooltip,
  formatTrendRangeSummary,
  getChartAxisEndpointLabels,
  getTrendBucketMode,
  getTrendChartUnitLabel,
  type CumulativeSubscriberTrendPoint,
} from '../../lib/connectionDashboard';
import { useDashboardTrendDays, useSyncedChartHover } from '../../context/DashboardChartHoverContext';
import ChartTodayMarker from './ChartTodayMarker';
import ChartHoverHighlight from './ChartHoverHighlight';

type SubscriberTotalTrendChartProps = {
  createdAtValues: string[];
  className?: string;
  onViewAll?: () => void;
};

const CHART_WIDTH = 400;
const CHART_HEIGHT = 160;

const SubscriberTotalTrendChart: React.FC<SubscriberTotalTrendChartProps> = ({
  createdAtValues,
  className = '',
  onViewAll,
}) => {
  const { dayCount } = useDashboardTrendDays();

  const points = useMemo(
    () => buildCumulativeSubscriberTrend(createdAtValues, dayCount),
    [createdAtValues, dayCount],
  );

  const bucketMode = getTrendBucketMode(dayCount);
  const axisLabels = useMemo(
    () => getChartAxisEndpointLabels(points, bucketMode),
    [points, bucketMode],
  );
  const unitLabel = getTrendChartUnitLabel(dayCount);

  const { displayHoverIndex, updateHoverAtIndex, clearHover, isHoverSource } = useSyncedChartHover(
    'subscribers',
    dayCount,
    bucketMode,
    points,
  );

  const todayX = useMemo(() => {
    const idx = findCurrentPeriodIndex(points, dayCount);
    if (idx == null) return null;
    return chartXAtPeriodIndex(idx, points.length, CHART_WIDTH);
  }, [points, dayCount]);

  const { coords, linePath, areaPath, minY, maxY } = useMemo(() => {
    if (points.length === 0) {
      return {
        coords: [] as { x: number; y: number; point: CumulativeSubscriberTrendPoint }[],
        linePath: '',
        areaPath: '',
        minY: 0,
        maxY: 1,
      };
    }

    const values = points.map((p) => p.total);
    const minV = values[0] ?? 0;
    const maxV = Math.max(...values, minV + 1);
    const range = Math.max(maxV - minV, 1);

    const nextCoords = points.map((p, i) => {
      const x =
        points.length <= 1 ? CHART_WIDTH / 2 : (i / (points.length - 1)) * CHART_WIDTH;
      const y = CHART_HEIGHT - ((p.total - minV) / range) * CHART_HEIGHT;
      return { x, y, point: p };
    });

    const line = nextCoords
      .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
      .join(' ');
    const area = `${line} L ${nextCoords[nextCoords.length - 1].x.toFixed(1)} ${CHART_HEIGHT.toFixed(1)} L ${nextCoords[0].x.toFixed(1)} ${CHART_HEIGHT.toFixed(1)} Z`;

    return {
      coords: nextCoords,
      linePath: line,
      areaPath: area,
      minY: minV,
      maxY: maxV,
    };
  }, [points]);

  const hovered = displayHoverIndex != null ? points[displayHoverIndex] : null;
  const hoverXPercent =
    displayHoverIndex != null && coords[displayHoverIndex]
      ? (coords[displayHoverIndex].x / CHART_WIDTH) * 100
      : null;

  const pickNearestIndex = (clientX: number, rect: DOMRect) => {
    if (coords.length === 0) return null;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const xInViewBox = ratio * CHART_WIDTH;
    let nearest = 0;
    let minDist = Infinity;
    coords.forEach((c, i) => {
      const dist = Math.abs(c.x - xInViewBox);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    return nearest;
  };

  if (points.length === 0) {
    return (
      <div className={`flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 ${className}`}>
        No subscriber data yet
      </div>
    );
  }

  const lastRealPoint = [...points].reverse().find((p) => !p.isFuture) ?? points[points.length - 1];
  const currentTotal = lastRealPoint?.total ?? 0;
  const startTotal = points[0]?.total ?? 0;
  const netGrowth = currentTotal - startTotal;

  return (
    <div className={`relative w-full min-w-0 min-h-[7.5rem] ${className}`}>
      <div className="absolute inset-0 z-0">
        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          preserveAspectRatio="none"
          className="w-full h-full block cursor-crosshair"
          role="img"
          aria-label={
            bucketMode === '5min'
              ? 'Total subscribers per 5 minutes over the last hour'
              : bucketMode === 'hourly'
                ? 'Total subscribers per hour over the last 6 hours'
                : bucketMode === 'weekly'
                  ? `Total subscribers by week over the last ${dayCount} days`
                  : `Total subscribers over the last ${dayCount} days`
          }
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            updateHoverAtIndex(pickNearestIndex(e.clientX, rect));
          }}
          onMouseLeave={clearHover}
        >
          <defs>
            <linearGradient id="subscriberTotalTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(59 130 246)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="rgb(59 130 246)" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {coords.map((c, i) =>
            i > 0 ? (
              <line
                key={`sub-week-grid-${c.point.periodStart}`}
                x1={c.x}
                y1={0}
                x2={c.x}
                y2={CHART_HEIGHT}
                stroke="rgb(203 213 225)"
                strokeWidth="1"
                strokeOpacity="0.55"
                vectorEffect="non-scaling-stroke"
              />
            ) : null,
          )}
          <path d={areaPath} fill="url(#subscriberTotalTrendFill)" />
          <path
            d={linePath}
            fill="none"
            stroke="rgb(59 130 246)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <ChartTodayMarker x={todayX} height={CHART_HEIGHT} />
          {displayHoverIndex != null && coords[displayHoverIndex] ? (
            <ChartHoverHighlight
              x={coords[displayHoverIndex].x}
              height={CHART_HEIGHT}
              dots={[{ y: coords[displayHoverIndex].y, fill: 'rgb(59 130 246)' }]}
            />
          ) : null}
        </svg>
      </div>

      <div className="absolute top-1.5 left-2 right-2 z-10 flex items-start justify-between gap-2 pointer-events-none">
        <div className="min-w-0">
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 drop-shadow-sm">
            Total subscribers{unitLabel ? ` · ${unitLabel}` : ''}
          </span>
          <div className="mt-0.5 text-lg font-bold tabular-nums text-gray-900 dark:text-white leading-none drop-shadow-sm">
            {currentTotal.toLocaleString()}
          </div>
          <div className="mt-0.5 text-[10px] text-gray-600 dark:text-gray-400">
            {netGrowth > 0 ? (
              <span className="text-blue-600 dark:text-blue-400 font-semibold">+{netGrowth}</span>
            ) : (
              <span>0</span>
            )}{' '}
            in {formatTrendRangeSummary(dayCount)}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onViewAll ? (
            <button
              type="button"
              onClick={onViewAll}
              className="pointer-events-auto inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors backdrop-blur-sm"
            >
              View all
            </button>
          ) : null}
        </div>
      </div>

      {isHoverSource && hovered && hoverXPercent != null ? (
        <div
          className="pointer-events-none absolute top-7 z-20 -translate-x-1/2 rounded-md border border-gray-200 dark:border-gray-600 bg-white/95 dark:bg-gray-800/95 px-2 py-1 shadow-sm whitespace-nowrap backdrop-blur-sm"
          style={{ left: `${hoverXPercent}%` }}
        >
          <div className="text-[10px] font-medium text-gray-900 dark:text-gray-100">
            {formatTrendPeriodTooltip(hovered.periodStart, bucketMode)}
          </div>
          <div className="text-[10px] tabular-nums text-gray-600 dark:text-gray-300">
            {hovered.isFuture ? (
              <span className="text-gray-500 dark:text-gray-400">No data yet</span>
            ) : (
              <>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {hovered.total.toLocaleString()}
                </span>{' '}
                total
                {hovered.added > 0 ? (
                  <>
                    {' '}
                    ·{' '}
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      +{hovered.added}
                      {bucketMode === 'weekly' ? ' wk' : bucketMode === 'hourly' ? ' hr' : bucketMode === '5min' ? ' /5m' : ''}
                    </span>
                  </>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}

      <div className="absolute bottom-1 left-2 right-2 z-10 flex justify-between text-[9px] text-gray-400/90 dark:text-gray-500 tabular-nums pointer-events-none">
        {bucketMode === 'hourly' || bucketMode === '5min' ? (
          <>
            <span className="drop-shadow-sm">{axisLabels.start}</span>
            <span className="drop-shadow-sm">{axisLabels.end}</span>
          </>
        ) : (
          <>
            <span className="drop-shadow-sm">{startTotal.toLocaleString()}</span>
            <span className="drop-shadow-sm">{currentTotal.toLocaleString()}</span>
          </>
        )}
      </div>

      <span className="sr-only">
        Subscriber total ranged from {minY.toLocaleString()} to {maxY.toLocaleString()} over the chart period.
      </span>
    </div>
  );
};

export default SubscriberTotalTrendChart;
